import { useMemo, useState, useEffect } from "react";

/**
 * Date picker: Día (input numérico) / Mes (select) / Año (input numérico).
 *  - Día y Año se escriben manualmente (sin desplegable).
 *  - Mes sigue siendo un <select> con los 12 meses.
 *
 * Props:
 *  - value:    string "YYYY-MM-DD" (ISO) o ""
 *  - onChange: (isoString) => void  — se llama con la fecha completa VÁLIDA, o "" si aún está incompleta/incorrecta
 *  - minYear:  default 1900
 *  - maxYear:  default current year
 *  - testId:   prefix para data-testid
 */
const MESES = [
  { v: "01", l: "Enero" }, { v: "02", l: "Febrero" }, { v: "03", l: "Marzo" },
  { v: "04", l: "Abril" }, { v: "05", l: "Mayo" }, { v: "06", l: "Junio" },
  { v: "07", l: "Julio" }, { v: "08", l: "Agosto" }, { v: "09", l: "Septiembre" },
  { v: "10", l: "Octubre" }, { v: "11", l: "Noviembre" }, { v: "12", l: "Diciembre" },
];

export default function DateDropdown({ value = "", onChange, minYear = 1900, maxYear, testId = "date" }) {
  const currentYear = new Date().getFullYear();
  const maxY = maxYear ?? currentYear;

  // Estado local: conserva las selecciones parciales (día/mes/año) aunque la fecha aún no esté completa.
  const [parts, setParts] = useState(() => {
    const [yy = "", mm = "", dd = ""] = (value || "").split("-");
    return { y: yy, m: mm, d: dd };
  });

  // Sincroniza con el valor del padre cuando cambia (ej. al editar un beneficiario existente).
  useEffect(() => {
    const [yy = "", mm = "", dd = ""] = (value || "").split("-");
    setParts((prev) => (prev.y === yy && prev.m === mm && prev.d === dd ? prev : { y: yy, m: mm, d: dd }));
  }, [value]);

  const { y, m, d } = parts;

  const daysInMonth = useMemo(() => {
    if (!y || y.length !== 4 || !m) return 31;
    return new Date(Number(y), Number(m), 0).getDate();
  }, [y, m]);

  // ¿Los 3 campos forman una fecha válida?
  const isComplete = (p) => {
    if (!p.d || !p.m || !p.y || p.y.length !== 4) return false;
    const yy = Number(p.y), dd = Number(p.d);
    if (yy < minYear || yy > maxY) return false;
    const maxDay = new Date(yy, Number(p.m), 0).getDate();
    if (dd < 1 || dd > maxDay) return false;
    return true;
  };

  const emit = (next) => {
    setParts(next);
    if (isComplete(next)) {
      onChange(`${next.y}-${next.m}-${String(next.d).padStart(2, "0")}`);
    } else {
      onChange("");
    }
  };

  // Día: solo dígitos, máximo 2, tope 31.
  const onDia = (raw) => {
    let v = (raw || "").replace(/\D/g, "").slice(0, 2);
    if (v && Number(v) > 31) v = "31";
    emit({ ...parts, d: v });
  };

  // Año: solo dígitos, máximo 4.
  const onAnio = (raw) => {
    const v = (raw || "").replace(/\D/g, "").slice(0, 4);
    emit({ ...parts, y: v });
  };

  const onMes = (v) => emit({ ...parts, m: v });

  const fieldStyle = { padding: "10px 8px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, background: "white", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr", gap: 8 }}>
      <input
        type="number" inputMode="numeric" min={1} max={31} placeholder="Día"
        value={d} onChange={(e) => onDia(e.target.value)}
        style={fieldStyle} data-testid={`${testId}-dia`}
      />
      <select
        value={m || ""} onChange={(e) => onMes(e.target.value)}
        style={{ ...fieldStyle, background: "white" }} data-testid={`${testId}-mes`}
      >
        <option value="">Mes</option>
        {MESES.map((mm) => (
          <option key={mm.v} value={mm.v}>{mm.l}</option>
        ))}
      </select>
      <input
        type="number" inputMode="numeric" min={minYear} max={maxY} placeholder="Año"
        value={y} onChange={(e) => onAnio(e.target.value)}
        style={fieldStyle} data-testid={`${testId}-anio`}
      />
    </div>
  );
}
